import './style.scss';
import React from 'react';
import { Button } from 'react-bootstrap';
import { FaEnvelope, FaCheckSquare, FaPhone } from 'react-icons/fa';
import { createStyles, Theme, makeStyles } from '@material-ui/core/styles';
import Skeleton from '@material-ui/lab/Skeleton';
import { terciaryColor } from 'designTokens/colors';
import DateUtils from 'utils/DateUtils';
import CheckBox from 'atoms/CheckBox';
import { personPlaceHolder } from 'designTokens/assets/images';
import LinkedInIcon from 'atoms/LinkedInIcon';
import { FormattingUtils } from "utils/index";
import { LightPrimaryButton, PrimaryButton } from 'atoms/StyledComponents';
import PhoneNumber from 'awesome-phonenumber';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    link_hover: {
      '&:hover': {
        textDecoration: 'underline',
        color: terciaryColor,
        cursor: 'pointer',
      },
    },
  }),
);

interface Props {
  contact?: any;
  anonymizedContact?: any;
  isChecked: boolean;
  toggleContactChecked: (profile_url) => any;
  onGetContact: (profileUrl) => any;
}

function getContactSection(contact) {
  const phoneNum = (phoneNum, type) => {
    if (!phoneNum) {
      return null;
    }
    const pn = PhoneNumber(phoneNum, 'US');
    return type && type != "undefined"
      ? `${pn.getNumber('national')} (${type})`
      : pn.getNumber('national');
  };
  return (
    <p>
      <div>
        {contact.work_email ? contact.work_email : null}
      </div>
      <div>{phoneNum(contact.mobile_phone_1, "mobile")}</div>
      <div>{phoneNum(contact.mobile_phone_2, "mobile")}</div>
      <div>{phoneNum(contact.mobile_phone_3, "mobile")}</div>
      <div>{phoneNum(contact.professsional_phone_1, "professsional")}</div>
      <div>{phoneNum(contact.professsional_phone_2, "professsional")}</div>
      <div>{phoneNum(contact.professsional_phone_3, "professsional")}</div>
    </p>
  );
}

function getAnonymizedContactSection(anonymizedContact) {
  return (
    <p style={{ color: terciaryColor }}>
      {anonymizedContact ? (
        <div>
          {anonymizedContact?.has_email ? (
            <div>
              <FaEnvelope /> {getAnonymizedEmail()}
            </div>
          ) : null}
          {anonymizedContact?.has_mobile_phone_1 ? (
            <div>
              <FaPhone /> {getAnonymizedPhone()}
            </div>
          ) : null}
          {anonymizedContact?.has_mobile_phone_2 ? (
            <div>
              <FaPhone /> {getAnonymizedPhone()}
            </div>
          ) : null}
          {anonymizedContact?.has_mobile_phone_3 ? (
            <div>
              <FaPhone /> {getAnonymizedPhone()}
            </div>
          ) : null}
          {anonymizedContact?.has_professional_phone_1 ? (
            <div>
              <FaPhone /> {getAnonymizedPhone()}
            </div>
          ) : null}
          {anonymizedContact?.has_professional_phone_2 ? (
            <div>
              <FaPhone /> {getAnonymizedPhone()}
            </div>
          ) : null}
          {anonymizedContact?.has_professional_phone_3 ? (
            <div>
              <FaPhone /> {getAnonymizedPhone()}
            </div>
          ) : null}
        </div>
      ) : (
        <Skeleton variant="text" width={150} />
      )}
    </p>
  );
} 

function getAnonymizedEmail() {
  return `•••••••••••@•••••••`;
}

function getAnonymizedPhone() {
  return `(•••)•••-••••`;
}



const ContactCard = ({
  anonymizedContact,
  contact,
  isChecked,
  toggleContactChecked,
  onGetContact
}: Props) => {
  const classes = useStyles();

  const hasData =
    anonymizedContact == null ? 
      false :  
      (
        anonymizedContact.has_email || 
        anonymizedContact.has_mobile_phone_1 ||
        anonymizedContact.has_mobile_phone_2 || 
        anonymizedContact.has_mobile_phone_3 ||
        anonymizedContact.has_professional_phone_1 ||
        anonymizedContact.has_professional_phone_2 || 
        anonymizedContact.has_professional_phone_3
      );
  let monthsInRole;
  let roleDuration;
  if (anonymizedContact && anonymizedContact.current_role_start_date) {
    monthsInRole = DateUtils.getNumMonthsBetween(
      new Date(anonymizedContact.current_role_start_date),
      new Date(),
    );
    if (monthsInRole < 12) {
      roleDuration = `${monthsInRole} Mo.`;
    } else {
      roleDuration = `${Math.floor(monthsInRole / 12)} Yrs.`;
    }
  }

  let durationAtCompany;
  if (anonymizedContact && anonymizedContact.total_months_at_company) {
    if (anonymizedContact.total_months_at_company < 12) {
      durationAtCompany = `${anonymizedContact.total_months_at_company} Mo.`;
    } else {
      durationAtCompany = `${Math.floor(
        anonymizedContact.total_months_at_company / 12,
      )} Yrs.`;
    }
  }

  return (
    <div className={`dev-producer-card`}>
      <div className="cell" style={{ position: "relative", width: '5%' }}>
        <div style={{ position: "absolute", top: "30%" }}>
          <CheckBox 
            isChecked={isChecked}
            isDisabled={false}
            onChange={() => toggleContactChecked(anonymizedContact?.employee_profile_url)}
          />
        </div>
      </div>
      
      <div className="cell" style={{ width: '35%', minWidth: '100px', textAlign: 'left' }}>
        <div className="user">
          <div className="u-icon">
            {anonymizedContact?.profile_pic ? (
              <img
                src={anonymizedContact?.profile_pic}
                onError={({ currentTarget }) => {
                  currentTarget.onerror = null; // prevents looping
                  currentTarget.src = personPlaceHolder;
                }}
                style={{
                  borderRadius: '50%',
                  width: '75px',
                  height: '75px',
                  maxWidth: 'none',
                }}
                alt=""
              />
            ) : (
              <img
                src={personPlaceHolder}
                style={{
                  borderRadius: '50%',
                  width: '75px',
                  height: '75px',
                  maxWidth: 'none',
                }}
                alt=""
              />
            )}
          </div>
          <div className="u-infos">
            {anonymizedContact ? (
              <div>
                <a
                  href={`${anonymizedContact.employee_profile_url ? `https://${anonymizedContact.employee_profile_url}` : ''}`}
                  className={classes.link_hover}
                  target="_blank"
                  rel="noreferrer"
                >
                  {anonymizedContact?.employee_name} <LinkedInIcon />
                </a>
                <span className="u-experience-title">{anonymizedContact?.experience_title}</span>
                <span className="u-location">{`
                  ${anonymizedContact?.employee_location
                    ?.replace(', United States', '')
                    .replace('United States', '')}
                `}</span>
              </div>
            ) : (
              <div>
                <Skeleton variant="text" width={100} />
                <Skeleton variant="text" width={150} />
                <Skeleton variant="text" width={175} />
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="cell" style={{ width: '20%', textAlign: 'right' }}>
        <div className="user">
          <div className="u-infos">
            {
              anonymizedContact ? (
                <div style={{ textAlign: "left" }}>
                    <a
                      href={`${anonymizedContact.company_profile_url ? `https://${anonymizedContact.company_profile_url}` : ''}`}
                      className={classes.link_hover}
                      target="_blank"
                      rel="noreferrer"
                      style={{ display: "block"}}
                    >
                      {anonymizedContact?.company_name ? FormattingUtils.formatUpperCasing(anonymizedContact?.company_name) : null} <LinkedInIcon />
                    </a>
                    {/* <p>{contact?.company_type ? FormattingUtils.formatUpperCasing(contact?.company_type) : null}</p> */}
                    <div>
                      {roleDuration ? <p>{roleDuration} in Role</p> : null}
                      {durationAtCompany ? <p>{durationAtCompany} at Company</p> : null}
                    </div>
                </div>
              ) : (
                <div>
                  <Skeleton variant="text" width={100} />
                  {/* <Skeleton variant="text" width={150} /> */}
                  <Skeleton variant="text" width={100} />
                  <Skeleton variant="text" width={150} />
                </div>
              )
            }
        </div>
        </div>
      </div>

      <div className="cell" style={{ width: '20%' }}>
        {
          contact
            ? getContactSection(contact)
            : getAnonymizedContactSection(anonymizedContact)
        }
      </div>

      <div className="cell" style={{ width: '20%', textAlign: "center" }}>
        {!contact ? (
            hasData ? (
              <PrimaryButton
                onClick={() => {
                  if (!hasData) {
                    return;
                  }
                  onGetContact(anonymizedContact?.employee_profile_url);
                }}
                style={{ minWidth: '120px' }}
              >
                Get Contact
              </PrimaryButton>
            ) : (
              <LightPrimaryButton disabled>
                Get Contact
              </LightPrimaryButton>
            )
          ) : (
            <Button className="checked-btn" disabled>
              <FaCheckSquare className="ico" />
            </Button>
          )}
      </div>

    </div>
  );
};

export default ContactCard;
